import { Loader2, Pin, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { MAX_BODY_LENGTH, MAX_TITLE_LENGTH } from '../../../shared/validation';
import { adminApi, type AdminAnnouncement } from '../../lib/api';
import { Meldung } from '../../lib/Meldung';

const dateFormat = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium' });

interface FormState {
  id: string | null;
  title: string;
  body: string;
  validUntil: string;
  pinned: boolean;
}

const EMPTY_FORM: FormState = { id: null, title: '', body: '', validUntil: '', pinned: false };

/** `valid_until` als ISO-Timestamp -> Wert fuer <input type="date">. */
function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export function AnnouncementsPanel() {
  const [items, setItems] = useState<AdminAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
   * Vorher gab es im ganzen Panel keine einzige Erfolgsmeldung: nach dem
   * Speichern aenderte sich nur die Liste, beim Anpinnen gar nichts Sichtbares.
   */
  const [info, setInfo] = useState<string | null>(null);

  // Nie mehr als eine Meldung gleichzeitig; der Fehler hat Vorrang.
  const meldung = error
    ? ({ art: 'error', text: error } as const)
    : info
      ? ({ art: 'success', text: info } as const)
      : null;

  const meldungSchliessen = useCallback(() => {
    setError(null);
    setInfo(null);
  }, []);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  async function reload() {
    setLoading(true);
    try {
      const data = await adminApi.listAnnouncements();
      setItems(data.announcements);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void reload();
  }, []);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setInfo(null);
    // Ende des gewaehlten Tages, nicht Mitternacht davor.
    const validUntil = form.validUntil ? new Date(`${form.validUntil}T23:59:59`).toISOString() : null;

    try {
      const payload = {
        title: form.title.trim(),
        body_md: form.body,
        valid_until: validUntil,
        pinned: form.pinned,
      };
      const geaendert = Boolean(form.id);
      if (form.id) await adminApi.updateAnnouncement({ id: form.id, ...payload });
      else await adminApi.createAnnouncement(payload);
      setForm(EMPTY_FORM);
      await reload();
      setInfo(geaendert ? 'Ankuendigung gespeichert.' : 'Ankuendigung angelegt.');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }

  async function togglePin(item: AdminAnnouncement) {
    setPending(item.id);
    setError(null);
    setInfo(null);
    try {
      await adminApi.updateAnnouncement({
        id: item.id,
        title: item.title,
        body_md: item.body_md,
        valid_until: item.valid_until,
        pinned: !item.pinned,
      });
      await reload();
      setInfo(item.pinned ? `„${item.title}" ist nicht mehr angepinnt.` : `„${item.title}" ist angepinnt.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(null);
    }
  }

  async function remove(item: AdminAnnouncement) {
    if (!window.confirm(`„${item.title}" wirklich löschen?`)) return;
    setPending(item.id);
    setError(null);
    setInfo(null);
    try {
      await adminApi.deleteAnnouncement(item.id);
      if (form.id === item.id) setForm(EMPTY_FORM);
      await reload();
      setInfo(`„${item.title}" wurde geloescht.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(null);
    }
  }

  return (
    <div className="stack">
      <section className="card">
        <h2>{form.id ? 'Ankündigung bearbeiten' : 'Neue Ankündigung'}</h2>
        <p className="card__meta">
          Markdown: **fett**, *kursiv*, `code`, [Link](https://…), Listen mit „- ", Überschriften
          mit „# ".
        </p>
        <form onSubmit={submit}>
          <label className="field">
            <span className="field__label">Titel</span>
            <input
              className="input"
              type="text"
              required
              maxLength={MAX_TITLE_LENGTH}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </label>
          <label className="field">
            <span className="field__label">Text (Markdown)</span>
            <textarea
              className="textarea"
              required
              maxLength={MAX_BODY_LENGTH}
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
            />
          </label>
          <div className="form__row form__row--2">
            <label className="field">
              <span className="field__label">Gültig bis (optional)</span>
              <input
                className="input"
                type="date"
                value={form.validUntil}
                onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
              />
            </label>
            <label className="checkbox">
              <input
                type="checkbox"
                checked={form.pinned}
                onChange={(e) => setForm({ ...form, pinned: e.target.checked })}
              />
              Oben anpinnen
            </label>
          </div>
          <div className="form__actions">
            <button type="submit" className="px-btn px-btn--sm" disabled={saving}>
              {saving ? (
                <Loader2 size={16} className="spin" aria-hidden="true" />
              ) : (
                <Plus size={16} aria-hidden="true" />
              )}
              {form.id ? 'Speichern' : 'Anschlagen'}
            </button>
            {form.id && (
              <button
                type="button"
                className="px-btn px-btn--ghost px-btn--sm"
                onClick={() => setForm(EMPTY_FORM)}
                disabled={saving}
              >
                Abbrechen
              </button>
            )}
          </div>
        </form>
      </section>

      <Meldung inhalt={meldung} onClose={meldungSchliessen} />

      <section className="card">
        <div className="section__head">
          <h2>Am Brett</h2>
          <span className="card__meta">{items.length}</span>
        </div>

        {loading ? (
          <div className="empty">Wird geladen …</div>
        ) : items.length === 0 ? (
          <div className="empty">Noch nichts angeschlagen.</div>
        ) : (
          items.map((item) => {
            const expired = item.valid_until ? new Date(item.valid_until) < new Date() : false;
            return (
              <div className="userrow" key={item.id}>
                <div className="userrow__top">
                  <div>
                    <div className="userrow__mail">{item.title}</div>
                    <div className="card__meta">
                      {dateFormat.format(new Date(item.created_at))}
                      {item.valid_until
                        ? ` · gültig bis ${dateFormat.format(new Date(item.valid_until))}`
                        : ' · unbegrenzt'}
                    </div>
                    <div className="userrow__tags">
                      {item.pinned && <span className="badge-pill badge-pill--pink">angepinnt</span>}
                      {expired && <span className="badge-pill badge-pill--muted">abgelaufen</span>}
                    </div>
                  </div>
                </div>
                <div className="form__actions">
                  <button
                    type="button"
                    className="px-btn px-btn--ghost px-btn--sm"
                    disabled={pending === item.id}
                    onClick={() =>
                      setForm({
                        id: item.id,
                        title: item.title,
                        body: item.body_md,
                        validUntil: toDateInput(item.valid_until),
                        pinned: item.pinned,
                      })
                    }
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    className="px-btn px-btn--ghost px-btn--sm"
                    disabled={pending === item.id}
                    onClick={() => void togglePin(item)}
                  >
                    <Pin size={14} aria-hidden="true" />
                    {item.pinned ? 'Loslösen' : 'Anpinnen'}
                  </button>
                  <button
                    type="button"
                    className="px-btn px-btn--danger px-btn--sm"
                    disabled={pending === item.id}
                    onClick={() => void remove(item)}
                  >
                    <Trash2 size={14} aria-hidden="true" />
                    Löschen
                  </button>
                </div>
              </div>
            );
          })
        )}
      </section>
    </div>
  );
}
